import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './TagsHierarchyChooser.module.scss';
import { ReactComponent as ExpandIcon } from './assets/chevron.svg';
import { FilterLoader } from '../Filters/components/FilterLoader';
import { isUkLocale } from '../TagsTree';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';

export interface SourceTagLevelEntry {
  name_uk: string | null;
  name_en: string | null;
  tag: string;
}

export interface SourceTag {
  full_path: string;
  levels: number;
  level_0: SourceTagLevelEntry | null;
  level_1: SourceTagLevelEntry | null;
  level_2: SourceTagLevelEntry | null;
  level_3: SourceTagLevelEntry | null;
  level_4: SourceTagLevelEntry | null;
  name_uk_full: string[];
  name_en_full: string[];
}

interface SourceTagsResponse {
  objects?: SourceTag[];
  total?: number;
}

interface TreeNode {
  fullPath: string;
  label: string;
  depth: number;
  tag: SourceTag;
  children: TreeNode[];
}

const getAllPaths = (node: TreeNode): string[] => {
  const paths: string[] = [node.fullPath];
  node.children.forEach((child) => paths.push(...getAllPaths(child)));
  return paths;
};

const getEntryLabel = (entry: SourceTagLevelEntry | null, fallback: string): string => {
  if (!entry) return fallback;
  if (isUkLocale()) {
    return entry.name_uk || entry.name_en || fallback;
  }
  return entry.name_en || entry.name_uk || fallback;
};

const buildTree = (tags: SourceTag[]): TreeNode[] => {
  if (!Array.isArray(tags) || tags.length === 0) return [];

  // Sort tags by full_path to ensure ancestors are processed before descendants.
  const sorted = [...tags].sort((a, b) => {
    const ap = a?.full_path || '';
    const bp = b?.full_path || '';
    return ap.localeCompare(bp);
  });

  const nodesByPath = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  sorted.forEach((tag) => {
    if (!tag?.full_path) return;
    const depth = Math.max(0, (tag.levels || 1) - 1);
    const entry = tag[`level_${depth}` as keyof SourceTag] as SourceTagLevelEntry | null;
    const label = getEntryLabel(entry, tag.full_path);

    const node: TreeNode = {
      fullPath: tag.full_path,
      label,
      depth,
      tag,
      children: [],
    };
    nodesByPath.set(tag.full_path, node);

    // Try to attach to parent. Parent path is everything before the last `.`.
    const lastDot = tag.full_path.lastIndexOf('.');
    if (lastDot > 0) {
      const parentPath = tag.full_path.slice(0, lastDot);
      const parent = nodesByPath.get(parentPath);
      if (parent) {
        parent.children.push(node);
        return;
      }
    }
    roots.push(node);
  });

  return roots;
};

interface TagsHierarchyChooserProps {
  selected: string[];
  onChange: (next: string[]) => void;
  /**
   * Optional preloaded tags. If omitted the component fetches `/API/v1/source_tags/public`.
   */
  items?: SourceTag[] | null;
  disabled?: boolean;
  /**
   * Initial expanded state. By default top-level groups start collapsed.
   * Pass `true` to expand all on mount.
   */
  initiallyExpanded?: boolean;
  style?: React.CSSProperties;
  /** Visual style variant. `'modern'` (default) uses the spacious panel style;
   *  `'legacy'` uses the compact sidebar-filter style. */
  mode?: 'legacy' | 'modern';
}

interface TreeItemProps {
  node: TreeNode;
  selected: string[];
  expandedPaths: Set<string>;
  toggleExpanded: (path: string) => void;
  toggleNode: (node: TreeNode) => void;
  disabled: boolean;
  mode: 'legacy' | 'modern';
}

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  selected,
  expandedPaths,
  toggleExpanded,
  toggleNode,
  disabled,
  mode,
}) => {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedPaths.has(node.fullPath);
  const isTopLevel = node.depth === 0;

  const allPaths = getAllPaths(node);
  const selectedCount = allPaths.filter((p) => selected.includes(p)).length;
  const isChecked = selectedCount === allPaths.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < allPaths.length;

  return (
    <div className={styles.treeNode}>
      <div className={classNames(styles.treeRow, isChecked && styles.checked)}>
        {hasChildren ? (
          <button
            type="button"
            className={styles.expandIcon}
            onClick={() => toggleExpanded(node.fullPath)}
            disabled={disabled}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ExpandIcon
              className={classNames(styles.expandIcon, isExpanded && styles.expanded)}
              aria-hidden="true"
            />
          </button>
        ) : (
          ''
        )}

        <label className={classNames(styles.treeLabel, isTopLevel && styles.topLevel)}>
          {mode === 'modern' ? (
            <span className={styles.checkboxWrapper}>
              <input
                type="checkbox"
                disabled={disabled}
                checked={isChecked}
                ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
                onChange={() => toggleNode(node)}
                className={styles.checkboxInput}
              />
              <span className={styles.checkboxCustom} aria-hidden="true" />
            </span>
          ) : (
            <input
              type="checkbox"
              disabled={disabled}
              checked={isChecked}
              ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
              onChange={() => toggleNode(node)}
            />
          )}
          <span title={node.fullPath}>{node.label}</span>
        </label>
      </div>

      {hasChildren && isExpanded && (
        <div className={styles.treeChildren}>
          {node.children.map((child) => (
            <TreeItem
              key={child.fullPath}
              node={child}
              selected={selected}
              expandedPaths={expandedPaths}
              toggleExpanded={toggleExpanded}
              toggleNode={toggleNode}
              disabled={disabled}
              mode={mode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TagsHierarchyChooser: React.FC<TagsHierarchyChooserProps> = ({
  selected,
  onChange,
  items,
  disabled = false,
  initiallyExpanded = false,
  style,
  mode = 'modern',
}) => {
  const { t } = useTranslation();
  const [fetchedTags, setFetchedTags] = useState<SourceTag[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch tags only when caller doesn't provide them.
  useEffect(() => {
    if (items !== undefined) return;
    let isActive = true;
    setIsLoading(true);
    API.fetch('GET', '/API/v1/source_tags/public')
      .then((data: SourceTagsResponse) => {
        if (!isActive) return;
        setFetchedTags(Array.isArray(data?.objects) ? data.objects : []);
      })
      .catch((error) => {
        showRequestError(error, 'Actor tags');
        if (isActive) setFetchedTags([]);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [items]);

  const effectiveTags: SourceTag[] | null = items !== undefined ? items ?? null : fetchedTags;
  const tree = useMemo(() => buildTree(effectiveTags || []), [effectiveTags]);

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set());

  // Initial expansion of all roots if requested.
  useEffect(() => {
    if (!initiallyExpanded) return;
    if (!tree.length) return;
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      tree.forEach((root) => next.add(root.fullPath));
      return next;
    });
  }, [initiallyExpanded, tree]);

  const toggleExpanded = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const toggleNode = useCallback(
    (node: TreeNode) => {
      const allPaths = getAllPaths(node);
      const allSelected = allPaths.every((p) => selected.includes(p));
      if (allSelected) {
        onChange(selected.filter((p) => !allPaths.includes(p)));
      } else {
        const toAdd = allPaths.filter((p) => !selected.includes(p));
        onChange([...selected, ...toAdd]);
      }
    },
    [selected, onChange],
  );

  if (isLoading && !effectiveTags) {
    return <FilterLoader />;
  }

  if (!tree.length) {
    return <div className={styles.empty}>{t('No tags available')}</div>;
  }

  return (
    <div
      className={classNames(
        styles.treeRoot,
        disabled && styles.disabled,
        mode === 'legacy' && styles.legacyMode,
      )}
      style={style}
    >
      {tree.map((root) => (
        <TreeItem
          key={root.fullPath}
          node={root}
          selected={selected}
          expandedPaths={expandedPaths}
          toggleExpanded={toggleExpanded}
          toggleNode={toggleNode}
          disabled={disabled}
          mode={mode}
        />
      ))}
    </div>
  );
};
